/**
 * ConceptMap (TASK-020 Phase 2) — la tab Mappa: indice dei capitoli
 * (`MacroIndex`, livello 1, invariato) + mappa disegnata del capitolo
 * (`ChapterMap` + `ConceptPanel` di `@arianna/reader-ui/concept-map`,
 * livelli 2/3, TASK-020 — sostituisce `ChapterOverview`/`ConceptPanel`
 * testuali del TASK-016).
 *
 * Drill state nell'URL (`?capitolo=&concetto=&famiglia=&pannello=`, vedi
 * `./concept-map/mapUrlState.ts`) via `window.history.pushState` +
 * `popstate` — MAI `router.visit` (farebbe un re-render server). Il salto a
 * un concetto esterno chiama `pushUrlState({chapterId, conceptId})`: la
 * fetch del nuovo capitolo riparte da sola, il back del browser è il
 * ritorno.
 *
 * `graph == null` (release senza concept-graph) resta lo stato vuoto del
 * TASK-016 Phase 3, invariato.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import Heading from '@arianna/design-system/Heading'
import Text from '@arianna/design-system/Text'
import { ChapterMap, ConceptPanel, useSpeech } from '@arianna/reader-ui/concept-map'
import type { RelationFamily } from '@arianna/reader-core'
import { buildPropositions, selectRestingEdges, type ChapterGraph } from '@arianna/reader-core'
import CourseLayout from './CourseLayout.js'
import type { PageComponent } from './inertiaPage.js'
import type { ConceptMapPageProps } from './types.js'
import { MapBreadcrumb } from './concept-map/MapBreadcrumb.js'
import { MacroIndex } from './concept-map/MacroIndex.js'
import { TRAIL_MAX_WIDTH } from './concept-map/IndexTrail.js'
import { fetchChapterExplosion, type ChapterExplosion } from './concept-map/mapApi.js'
import { resolveTutorSectionTargetFromSlugs } from './tutorNav.js'
import { sectionUrl } from './urls.js'
import {
  buildMapSearch,
  mapUrlStatesEqual,
  normalizeMapUrlState,
  parseMapUrlState,
  type MapUrlState,
} from './concept-map/mapUrlState.js'

// ---------------------------------------------------------------------------
// ConceptGraphGlyph — invariato dal TASK-016 Phase 3/4 (empty-state).
// ---------------------------------------------------------------------------
function ConceptGraphGlyph({ size = 96 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      stroke="var(--accent)"
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="6" y1="6" x2="16" y2="6" />
      <line x1="6" y1="6" x2="11" y2="16" />
      <line x1="16" y1="6" x2="11" y2="16" />
      <circle cx="6" cy="6" r="2" fill="var(--surface)" />
      <circle cx="16" cy="6" r="2" fill="var(--surface)" />
      <circle cx="11" cy="16" r="2" fill="var(--surface)" />
    </svg>
  )
}

function ConceptGraphGlyphSmall() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 22 22"
      fill="none"
      stroke="var(--accent)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="6" y1="6" x2="16" y2="6" />
      <line x1="6" y1="6" x2="11" y2="16" />
      <line x1="16" y1="6" x2="11" y2="16" />
      <circle cx="6" cy="6" r="2" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="11" cy="16" r="2" />
    </svg>
  )
}

function EmptyState() {
  return (
    <div
      data-testid="concept-map-empty"
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 'var(--space-6)',
        gap: 'var(--space-4)',
      }}
    >
      <ConceptGraphGlyph />
      <Heading variant="eyebrow">Mappa concettuale</Heading>
      <Heading variant="cardXl" style={{ maxWidth: 420 }}>
        Non ancora disponibile per questo manuale
      </Heading>
      <Text variant="body" style={{ maxWidth: 420 }}>
        La mappa dei concetti viene generata insieme al resto del materiale. Se questo manuale è
        stato preparato prima che la mappa esistesse, non verrà creata finché non viene rigenerato.
      </Text>
    </div>
  )
}

function ChapterLoading({ reducedMotion }: { reducedMotion: boolean | null }) {
  return (
    <div
      data-testid="concept-map-chapter-loading"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 280,
        gap: 'var(--space-3)',
      }}
    >
      {!reducedMotion && (
        <div
          aria-hidden="true"
          style={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            border: '3px solid var(--line)',
            borderTopColor: 'var(--accent)',
            animation: 'concept-map-spin 0.8s linear infinite',
          }}
        />
      )}
      <Text variant="meta">Carico i concetti del capitolo…</Text>
    </div>
  )
}

function ChapterError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      data-testid="concept-map-chapter-error"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 280,
        gap: 'var(--space-3)',
        textAlign: 'center',
        padding: 'var(--space-4)',
      }}
    >
      <Text variant="body" style={{ maxWidth: 360 }}>
        {message}
      </Text>
      <button
        type="button"
        onClick={onRetry}
        data-testid="concept-map-chapter-retry"
        style={{
          border: '1px solid var(--line)',
          background: 'var(--surface-2)',
          color: 'var(--ink)',
          borderRadius: 8,
          padding: '6px 14px',
          cursor: 'pointer',
        }}
      >
        Riprova
      </button>
    </div>
  )
}

const CONCEPT_MAP_STYLES = `
@keyframes concept-map-spin {
  to { transform: rotate(360deg); }
}
`

interface GraphExplorerProps {
  manualVersionId: number
  graph: NonNullable<ConceptMapPageProps['graph']>
  sectionSlugs: Record<string, string>
}

interface ChapterFetchState {
  chapterId: string
  retryToken: number
  status: 'ready' | 'error'
  data: ChapterExplosion | null
  error: string | null
}

function subParaIdFromBlockId(blockId: string): string {
  const slashIndex = blockId.lastIndexOf('/')
  return slashIndex === -1 ? blockId : blockId.slice(0, slashIndex)
}

function GraphExplorer({ manualVersionId, graph, sectionSlugs }: GraphExplorerProps) {
  const reducedMotion = useReducedMotion()
  const [panelDocked, setPanelDocked] = useState(true)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(min-width: 1024px)')
    const update = () => setPanelDocked(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  const speech = useSpeech('it')

  const [urlState, setUrlState] = useState<MapUrlState>(() =>
    parseMapUrlState(window.location.search),
  )
  const [chapterFetch, setChapterFetch] = useState<ChapterFetchState | null>(null)
  const [retryToken, setRetryToken] = useState(0)

  useEffect(() => {
    function onPopState() {
      setUrlState(parseMapUrlState(window.location.search))
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => {
    const chapterId = urlState.chapterId
    if (chapterId == null) return

    let cancelled = false
    fetchChapterExplosion(manualVersionId, chapterId)
      .then((data) => {
        if (cancelled) return
        setChapterFetch({ chapterId, retryToken, status: 'ready', data, error: null })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setChapterFetch({
          chapterId,
          retryToken,
          status: 'error',
          data: null,
          error: err instanceof Error ? err.message : 'Errore nel caricamento della mappa.',
        })
      })
    return () => {
      cancelled = true
    }
  }, [urlState.chapterId, manualVersionId, retryToken])

  const chapterLoading =
    urlState.chapterId != null &&
    (chapterFetch == null ||
      chapterFetch.chapterId !== urlState.chapterId ||
      chapterFetch.retryToken !== retryToken)
  const chapterData = !chapterLoading && chapterFetch?.status === 'ready' ? chapterFetch.data : null
  const chapterError =
    !chapterLoading && chapterFetch?.status === 'error' ? chapterFetch.error : null

  const chapterGraph: ChapterGraph | null = useMemo(() => {
    if (chapterData == null) return null
    return {
      nodes: chapterData.nodes,
      edges: chapterData.edges,
      externalNodes: chapterData.externalNodes,
    }
  }, [chapterData])

  const pushUrlState = useCallback((next: Partial<MapUrlState>) => {
    setUrlState((current) => {
      const normalized = normalizeMapUrlState({ ...current, ...next })
      if (mapUrlStatesEqual(current, normalized)) return current
      const url = `${window.location.pathname}${buildMapSearch(normalized)}${window.location.hash}`
      window.history.pushState(window.history.state, '', url)
      return normalized
    })
  }, [])

  const goToManualRoot = useCallback(
    () => pushUrlState({ chapterId: null, conceptId: null, family: 'all', panelOpen: false }),
    [pushUrlState],
  )
  const goToChapter = useCallback(
    (chapterId: string) =>
      pushUrlState({ chapterId, conceptId: null, family: 'all', panelOpen: false, recall: false }),
    [pushUrlState],
  )
  const onFocusChange = useCallback(
    (nodeId: string | null) => pushUrlState({ conceptId: nodeId, panelOpen: false }),
    [pushUrlState],
  )
  const onFamilyChange = useCallback(
    (family: RelationFamily | 'all') => pushUrlState({ family }),
    [pushUrlState],
  )
  const onOpenPanel = useCallback(
    (nodeId: string) => pushUrlState({ conceptId: nodeId, panelOpen: true }),
    [pushUrlState],
  )
  const onClosePanel = useCallback(() => pushUrlState({ panelOpen: false }), [pushUrlState])
  const onRecallChange = useCallback((recall: boolean) => pushUrlState({ recall }), [pushUrlState])
  const onGoToExternal = useCallback(
    (target: { chapterId: string; nodeId: string }) =>
      pushUrlState({
        chapterId: target.chapterId,
        conceptId: target.nodeId,
        family: 'all',
        panelOpen: false,
      }),
    [pushUrlState],
  )
  const retry = useCallback(() => setRetryToken((n) => n + 1), [])

  const onGoToManualBlock = useCallback(
    (blockId: string) => {
      const target = resolveTutorSectionTargetFromSlugs(
        sectionSlugs,
        subParaIdFromBlockId(blockId),
        blockId,
      )
      if (target == null) return
      window.location.assign(`${sectionUrl(manualVersionId, target.slug)}${target.anchor}`)
    },
    [manualVersionId, sectionSlugs],
  )

  const chapterLabel = useMemo(() => {
    if (urlState.chapterId == null) return null
    const macroNode = graph.nodes.find(
      (n) => n.level === 'macro' && n.chapterId === urlState.chapterId,
    )
    return macroNode?.label ?? urlState.chapterId
  }, [graph.nodes, urlState.chapterId])

  const chapterLabels = useMemo(() => {
    const map: Record<string, string> = {}
    for (const node of graph.nodes) {
      if (node.level === 'macro') map[node.chapterId] = node.label
    }
    return map
  }, [graph.nodes])

  const [prevChapterId, setPrevChapterId] = useState(urlState.chapterId)
  const [swapDirection, setSwapDirection] = useState<'deeper' | 'shallower'>('shallower')
  if (prevChapterId !== urlState.chapterId) {
    setSwapDirection(prevChapterId == null && urlState.chapterId != null ? 'deeper' : 'shallower')
    setPrevChapterId(urlState.chapterId)
  }
  const goingDeeper = swapDirection === 'deeper'

  const swapTransition = reducedMotion
    ? { duration: 0 }
    : goingDeeper
      ? { type: 'spring' as const, stiffness: 220, damping: 26 }
      : { duration: 0.15, ease: 'easeOut' as const }

  const propositions = useMemo(() => {
    if (chapterGraph == null || urlState.conceptId == null) return []
    const resting = selectRestingEdges(chapterGraph)
    return buildPropositions(chapterGraph, urlState.conceptId, 'it', resting)
  }, [chapterGraph, urlState.conceptId])

  return (
    <>
      <style>{CONCEPT_MAP_STYLES}</style>
      <MapBreadcrumb
        manualLabel="Manuale"
        chapterLabel={chapterLabel}
        conceptLabel={
          urlState.conceptId != null
            ? (chapterGraph?.nodes.find((n) => n.nodeId === urlState.conceptId)?.label ?? null)
            : null
        }
        onSelectManual={goToManualRoot}
        onSelectChapter={() => urlState.chapterId != null && goToChapter(urlState.chapterId)}
      />
      <div
        data-testid="concept-map-content"
        className="flex-1 overflow-y-auto"
        style={{
          position: 'relative',
          padding: '0 var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <AnimatePresence mode="wait">
          {urlState.chapterId == null ? (
            <motion.div
              key="macro"
              initial={reducedMotion ? false : { opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={swapTransition}
            >
              <div style={{ width: '100%', maxWidth: TRAIL_MAX_WIDTH, marginInline: 'auto' }}>
                <div
                  style={{
                    padding: 'var(--space-4) 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                  }}
                >
                  <ConceptGraphGlyphSmall />
                  <Heading variant="eyebrow">Mappa concettuale</Heading>
                </div>
                <Text variant="lead" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
                  Tocca un capitolo per esplorarne la mappa dei concetti.
                </Text>
                <MacroIndex nodes={graph.nodes} onSelectChapter={goToChapter} />
              </div>
            </motion.div>
          ) : (
            // Nessuna animazione motion su questo livello: ChapterMap ridisegna a
            // ogni tick d3 (setSimNodes) e affama i frame dell'animazione, che
            // restava a opacity ~0.35. Il livello capitolo appare senza fade.
            <div
              key={`chapter-${urlState.chapterId}`}
              style={{
                position: 'relative',
                flex: 1,
                minHeight: 480,
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                {chapterLoading && <ChapterLoading reducedMotion={reducedMotion} />}
                {!chapterLoading && chapterError != null && (
                  <ChapterError message={chapterError} onRetry={retry} />
                )}
                {!chapterLoading && chapterError == null && chapterGraph != null && (
                  <ChapterMap
                    graph={chapterGraph}
                    chapterLabel={chapterLabel ?? ''}
                    focusNodeId={urlState.conceptId}
                    family={urlState.family}
                    recall={urlState.recall}
                    onRecallChange={onRecallChange}
                    locale="it"
                    onFocusChange={onFocusChange}
                    onFamilyChange={onFamilyChange}
                    onOpenPanel={onOpenPanel}
                    onGoToManual={onGoToManualBlock}
                    onGoToExternal={onGoToExternal}
                    {...(speech.supported ? { onSpeak: speech.speak } : {})}
                    chapterLabels={chapterLabels}
                  />
                )}
              </div>
              {chapterGraph != null && urlState.conceptId != null && urlState.panelOpen && (
                <div
                  data-testid="concept-panel-slot"
                  style={
                    panelDocked
                      ? { flex: 'none', width: 360, display: 'flex', flexDirection: 'column' }
                      : { position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 5 }
                  }
                >
                  <ConceptPanel
                    graph={chapterGraph}
                    nodeId={urlState.conceptId}
                    propositions={propositions}
                    locale="it"
                    docked={panelDocked}
                    onClose={onClosePanel}
                    onGoToManual={onGoToManualBlock}
                    {...(speech.supported ? { onSpeak: speech.speak } : {})}
                    onGoToExternal={onGoToExternal}
                  />
                </div>
              )}
            </div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}

const ConceptMap: PageComponent<ConceptMapPageProps> = ({ shell, graph, sectionSlugs }) => {
  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden" data-testid="concept-map-page">
      {graph == null ? (
        <EmptyState />
      ) : (
        <GraphExplorer
          manualVersionId={shell.manualVersionId}
          graph={graph}
          sectionSlugs={sectionSlugs}
        />
      )}
    </div>
  )
}

ConceptMap.layout = (page) => <CourseLayout>{page}</CourseLayout>

export default ConceptMap
